import { useState } from "react";
import { Loader2, FileSpreadsheet } from "lucide-react";
import { useModelo303 } from "../api";
import { useAccountingStore } from "../store";
import { AccountingHeader } from "../components/AccountingHeader";
import type { IvaLinea } from "../types";

function eur(n: number): string {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);
}

const TRIMESTRES = [
  { v: 0, label: "Anual" },
  { v: 1, label: "1T (ene-mar)" },
  { v: 2, label: "2T (abr-jun)" },
  { v: 3, label: "3T (jul-sep)" },
  { v: 4, label: "4T (oct-dic)" },
];

function TablaIva({ lineas, color }: { lineas: IvaLinea[]; color: string }) {
  if (lineas.length === 0) {
    return <p className="text-sm text-slate-400 py-3">Sin cuotas en el periodo.</p>;
  }
  return (
    <table className="w-full text-sm">
      <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
        <tr>
          <th className="text-left py-2 font-bold">Tipo</th>
          <th className="text-right py-2 font-bold">Base imponible</th>
          <th className="text-right py-2 font-bold">Cuota</th>
        </tr>
      </thead>
      <tbody>
        {lineas.map((l) => (
          <tr key={l.tipo} className="border-t border-slate-100">
            <td className="py-2">
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border"
                style={{ borderColor: color, color }}
              >
                {l.tipo}%
              </span>
            </td>
            <td className="py-2 text-right font-mono">{eur(l.base)}</td>
            <td className="py-2 text-right font-mono font-bold">{eur(l.cuota)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Modelo303Page() {
  const { emisorId, ejercicio } = useAccountingStore();
  const [trimestre, setTrimestre] = useState(0);
  const { data, isLoading } = useModelo303(emisorId, ejercicio, trimestre);

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <AccountingHeader
        title="Contabilidad"
        subtitle="Modelo 303 · Autoliquidación de IVA."
        right={
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">
              Periodo
            </label>
            <select
              value={trimestre}
              onChange={(e) => setTrimestre(Number(e.target.value))}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold self-end"
            >
              {TRIMESTRES.map((t) => (
                <option key={t.v} value={t.v}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        }
      />

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Calculando…
        </div>
      )}

      {data && (
        <div className="flex-1 min-h-0 overflow-auto space-y-4">
          {/* Resultado destacado */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 shadow-soft">
              <div className="text-xs font-black uppercase tracking-wider text-emerald-700">
                IVA devengado (repercutido)
              </div>
              <div className="text-2xl font-black text-slate-900 mt-1">
                {eur(data.total_devengado)}
              </div>
              <div className="text-xs text-slate-500">
                Base {eur(data.total_base_devengado)}
              </div>
            </div>
            <div className="rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white p-5 shadow-soft">
              <div className="text-xs font-black uppercase tracking-wider text-sky-700">
                IVA deducible (soportado)
              </div>
              <div className="text-2xl font-black text-slate-900 mt-1">
                {eur(data.total_deducible)}
              </div>
              <div className="text-xs text-slate-500">
                Base {eur(data.total_base_deducible)}
              </div>
            </div>
            <div
              className={`rounded-2xl border p-5 shadow-soft text-white ${
                data.resultado >= 0
                  ? "border-brand-700/20 bg-gradient-to-br from-brand-700 to-emerald-600"
                  : "border-rose-300 bg-gradient-to-br from-rose-600 to-rose-500"
              }`}
            >
              <div className="text-xs font-black uppercase tracking-wider opacity-90">
                Resultado {data.resultado >= 0 ? "(a ingresar)" : "(a compensar)"}
              </div>
              <div className="text-2xl font-black mt-1">{eur(Math.abs(data.resultado))}</div>
              <div className="text-xs opacity-80 mt-0.5 flex items-center gap-1">
                <FileSpreadsheet size={12} /> Devengado − Deducible
              </div>
            </div>
          </div>

          {/* Detalle por tipo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
              <h3 className="font-black text-slate-900 mb-2">IVA Devengado por tipo</h3>
              <TablaIva lineas={data.devengado} color="#059669" />
            </div>
            <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
              <h3 className="font-black text-slate-900 mb-2">IVA Deducible por tipo</h3>
              <TablaIva lineas={data.deducible} color="#0284c7" />
            </div>
          </div>

          <p className="text-xs text-slate-400">
            Periodo: {new Date(data.desde).toLocaleDateString("es-ES")} —{" "}
            {new Date(data.hasta).toLocaleDateString("es-ES")}. El cálculo usa las subcuentas
            477xxxxxx (repercutido) y 472xxxxxx (soportado), excluyendo asientos de regularización
            contra Hacienda (475/470).
          </p>
        </div>
      )}
    </div>
  );
}
